import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import useExpress, { expressMutation } from '../../repository/react-query/useExpress';
import { restoreCache, restoreDeletedQueryCache, setDeletedQueryCache, updateQueryCache } from '../optimistic-update-utils';
import { buildLoopbackQuery } from '../../repository/queries/builders';
import axios from 'axios';

const baseEndpoint = '/trailers';

const getTrailersQuery = ({ path, includeDeleted, queryParams }) => {
    const { where = {}, ...rest } = queryParams;
    const params = {
        where: {
            ...(includeDeleted ? { deleted_at: 'any' } : { deleted_at: { eq: null } }),
            ...where,
        },
        ...rest,
    };

    const endpoint = path ? `${baseEndpoint}/${path}` : baseEndpoint;
    return useExpress({ endpoint, params });
};

export const useFetchTrailers = ({ path, includeDeleted, queryParams = {}, ...useQueryOptions }) => {
    const queryFn = getTrailersQuery({ path, includeDeleted, queryParams });
    const queryKey = ['trailer-data', { includeDeleted, ...queryParams }];
    return useQuery({ queryKey, queryFn, ...useQueryOptions });
};

export const useCreateTrailer = ({ key }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data }) => {
            return expressMutation({
                method: 'POST',
                endpoint: `${baseEndpoint}`,
                body: data,
            });
        },
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
};

export const useUpdateTrailer = ({ key }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => {
            return expressMutation({
                method: 'PATCH',
                endpoint: `${baseEndpoint}/${id}`,
                body: data,
            });
        },
        onMutate: (updatedItem) => updateQueryCache(queryClient, [key], { ...updatedItem.data }),
        onError: (_err, _, ctx) => restoreCache(queryClient, ctx),
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
};

export const useDeleteTrailer = ({ key }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id }) => {
            return expressMutation({
                method: 'DELETE',
                endpoint: `${baseEndpoint}/${id}`,
            });
        },
        onMutate: (updatedItem) => setDeletedQueryCache(queryClient, [key], updatedItem),
        onError: (_err, _, ctx) => restoreCache(queryClient, ctx),
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
};
